<template lang="pug">
  video.video-js.vjs-big-play-centered(
    ref="refVideo"
    v-bind="$attrs"
    :src="src"
    style="width: 100%")
    track(
      v-for="crtTrack in trackList"
      :kind="crtTrack.kind"
      :label="crtTrack.label"
      :src="crtTrack.src"
      :srcLang="crtTrack.srcLang"
      :default="crtTrack.default")
</template>

<script>
// lib
import videojs from 'video.js'
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount
} from '@vue/composition-api'
import 'video.js/dist/video-js.min.css'
import Vue from 'vue'

const DEFAULT_EVENTS = [
  'loadeddata',
  'canplay',
  'canplaythrough',
  'play',
  'pause',
  'waiting',
  'playing',
  'ended',
  'error',
  `touchstart`
]

export default {
  inheritAttrs: false,

  props: {
    src: String,
    type: String,
    isPlaying: Boolean,
    start: {
      type: Number,
      default: 0
    },
    crossOrigin: {
      type: String,
      default: ''
    },
    playsinline: {
      type: Boolean,
      default: false
    },
    customEventName: {
      type: String,
      default: 'statechanged'
    },
    events: {
      type: Array,
      default: () => []
    },
    globalOptions: {
      type: Object,
      default: () => ({
        // autoplay: false,
        controls: true,
        // preload: 'auto',
        // fluid: false,
        // muted: false,
        controlBar: {
          remainingTimeDisplay: false,
          playToggle: {},
          progressControl: {},
          fullscreenToggle: {},
          volumeMenuButton: {
            inline: false,
            vertical: true
          }
        },
        techOrder: ['html5'],
        plugins: {}
      })
    },
    globalEvents: {
      type: Array,
      default: () => []
    },
    trackList: {
      type: Array,
      default: () => []
    }
  },

  setup (props, context) {
    const refVideo = ref(null)
    const player = ref(null)

    const initialize = () => {
      dispose()
      // videojs options
      const videoOptions = Object.assign({}, props.globalOptions, props.options)
      // ios fullscreen
      if (props.playsinline) {
        refVideo.value.setAttribute('playsinline', props.playsinline)
        refVideo.value.setAttribute('webkit-playsinline', props.playsinline)
        refVideo.value.setAttribute('x5-playsinline', props.playsinline)
        refVideo.value.setAttribute('x5-video-player-type', 'h5')
        refVideo.value.setAttribute('x5-video-player-fullscreen', false)
      }
      // cross origin
      if (props.crossOrigin !== '') {
        refVideo.value.crossOrigin = props.crossOrigin
        refVideo.value.setAttribute('crossOrigin', props.crossOrigin)
      }
      // avoid error "VIDEOJS: ERROR: Unable to find plugin: __ob__"
      if (videoOptions.plugins) {
        delete videoOptions.plugins.__ob__
      }
      // videoOptions
      // player
      player.value = videojs(refVideo.value, context.attrs)
      player.value.src({
        type: props.type,
        src: props.src
      })
      player.value.ready(() => {
        // events
        const events = DEFAULT_EVENTS.concat(props.events).concat(props.globalEvents)
        // watch events
        const onEdEvents = {}
        for (let i = 0; i < events.length; i++) {
          if (
            typeof events[i] === 'string' &&
            onEdEvents[events[i]] === undefined
          ) {
            (event => {
              onEdEvents[event] = null
              player.value.on(event, () => {
                if (event === `ended`) {
                  context.emit(`update:is-playing`, false)
                }
                context.emit(event)
              })
            })(events[i])
          }
        }
        // watch timeupdate
        player.value.on('timeupdate', () => {
          context.emit(
            'current-time-updated',
            player.value.currentTime()
          )
          context.emit(
            'remaining-time-updated',
            player.value.remainingTime()
          )
        })
        watch(
          () => props.isPlaying,
          isPlaying => {
            if (isPlaying) {
              player.value.play()
            } else {
              player.value.pause()
            }
          }, {
            immediate: true
          }
        )

        // player ready
        context.emit('ready', player.value)
      })
    }

    const dispose = callback => {
      if (player.value?.dispose) {
        if (player.value.techName_ !== 'Flash') {
          player.value?.pause()
        }
        player.value.dispose()
        player.value = null
        callback && callback()
      }
    }

    onMounted(() => {
      watch(
        () => props.src,
        src => {
          if (src) {
            initialize()
          } else {
            dispose()
          }
        }, {
          immediate: true
        }
      )
    })

    onBeforeUnmount(
      () => {
        if (player.value) {
          dispose()
        }
      }
    )

    return {
      refVideo
    }
  }
}
</script>
